/**
 * Counter-based PRNG — Philox 4x32-10.
 *
 * PRD §6.2 requires bit-identical results for a given (project, seed, N)
 * regardless of how many workers run the job. That rules out any stateful
 * generator whose stream depends on scheduling, and it rules out
 * `Math.random()` outright (unseedable).
 *
 * Philox is a counter-based ("stateless") generator: the value at any position
 * is a pure function of (key, counter). Every worker can therefore compute the
 * draw for iteration `i`, variable `j` in isolation, with no coordination and
 * no per-worker stream bookkeeping. Sharding becomes a scheduling detail that
 * cannot affect results.
 *
 * Reference: Salmon et al., "Parallel Random Numbers: As Easy as 1, 2, 3"
 * (SC'11). Constants below are from that paper.
 */

const W0 = 0x9e3779b9; // golden ratio
const W1 = 0xbb67ae85; // sqrt(3) - 1

// Multiplier limbs. M0 = 0xd2511f53, M1 = 0xcd9e8d57, split into 16-bit halves
// so the 32x32 -> 64 multiply can be done in exact double arithmetic. JS has no
// 64-bit integer multiply on numbers, and BigInt is orders of magnitude too
// slow for a 10^7-iteration hot loop.
const M0_LO = 0x1f53;
const M0_HI = 0xd251;
const M1_LO = 0x8d57;
const M1_HI = 0xcd9e;

/** 2^-53, for converting a 53-bit integer to [0,1). */
const TWO_POW_NEG_53 = 1 / 9007199254740992;

/**
 * Philox4x32-10, allocation-free.
 *
 * The 32x32 -> 64 multiplies are inlined rather than factored into a helper
 * returning [hi, lo]: at 10 rounds x 2 multiplies x 10^7 draws, a two-element
 * array per multiply is 2x10^9 allocations, which dominated everything else in
 * the profile by an order of magnitude. Every intermediate below stays under
 * 2^53, where doubles are exact.
 *
 * Results are written into `dst` (length >= 4) to keep the caller allocation-free too.
 */
export function philox4x32(
  k0: number,
  k1: number,
  c0: number,
  c1: number,
  c2: number,
  c3: number,
  dst: Uint32Array,
): void {
  let x0 = c0 >>> 0;
  let x1 = c1 >>> 0;
  let x2 = c2 >>> 0;
  let x3 = c3 >>> 0;
  let key0 = k0 >>> 0;
  let key1 = k1 >>> 0;

  for (let round = 0; round < 10; round++) {
    // hi0/lo0 = M0 * x0
    const b0lo = x0 & 0xffff;
    const b0hi = x0 >>> 16;
    const a0p0 = M0_LO * b0lo;
    const a0p1 = M0_HI * b0lo;
    const a0p2 = M0_LO * b0hi;
    const a0p3 = M0_HI * b0hi;
    const mid0 = (a0p0 >>> 16) + (a0p1 & 0xffff) + (a0p2 & 0xffff);
    const lo0 = (((mid0 & 0xffff) << 16) | (a0p0 & 0xffff)) >>> 0;
    const hi0 = (a0p3 + (a0p1 >>> 16) + (a0p2 >>> 16) + (mid0 >>> 16)) >>> 0;

    // hi1/lo1 = M1 * x2
    const b1lo = x2 & 0xffff;
    const b1hi = x2 >>> 16;
    const a1p0 = M1_LO * b1lo;
    const a1p1 = M1_HI * b1lo;
    const a1p2 = M1_LO * b1hi;
    const a1p3 = M1_HI * b1hi;
    const mid1 = (a1p0 >>> 16) + (a1p1 & 0xffff) + (a1p2 & 0xffff);
    const lo1 = (((mid1 & 0xffff) << 16) | (a1p0 & 0xffff)) >>> 0;
    const hi1 = (a1p3 + (a1p1 >>> 16) + (a1p2 >>> 16) + (mid1 >>> 16)) >>> 0;

    const n0 = (hi1 ^ x1 ^ key0) >>> 0;
    const n2 = (hi0 ^ x3 ^ key1) >>> 0;
    x0 = n0;
    x1 = lo1;
    x2 = n2;
    x3 = lo0;

    // Key schedule: bump between rounds. Bumping after the final round is
    // harmless (the value is never read) and keeps the loop branch-free.
    key0 = (key0 + W0) >>> 0;
    key1 = (key1 + W1) >>> 0;
  }

  dst[0] = x0;
  dst[1] = x1;
  dst[2] = x2;
  dst[3] = x3;
}

/** Combine two 32-bit words into a uniform double in [0,1) with 53-bit resolution. */
function toUnit53(hi: number, lo: number): number {
  // 26 high bits + 27 low bits = 53 bits of mantissa.
  return ((hi >>> 6) * 134217728 + (lo >>> 5)) * TWO_POW_NEG_53;
}

/**
 * A seeded, position-addressable source of uniform variates.
 *
 * `at(iteration, slot)` is a pure function: the same coordinates always yield
 * the same value, for the life of the project file. Slot numbers are assigned
 * once per variable (see `sampling.ts`) and must stay stable, or previously
 * saved analyses stop reproducing.
 */
export class CounterRng {
  private readonly k0: number;
  private readonly k1: number;
  /** Per-instance scratch, so two Rngs in the same thread cannot collide. */
  private readonly words = new Uint32Array(4);

  constructor(seed: number) {
    // Spread a single user-facing integer seed across both key words so that
    // seeds 1, 2, 3... produce well-separated streams rather than adjacent ones.
    const s = seed >>> 0;
    this.k0 = s;
    this.k1 = (Math.imul(s, 0x9e3779b1) ^ 0x85ebca6b) >>> 0;
  }

  /**
   * One uniform in [0,1) for the given (iteration, slot).
   *
   * Iterations beyond 2^32 are addressed by the high counter word, so the
   * usable range is far beyond the 10^7 ceiling in PRD §4.6.
   */
  uniform(iteration: number, slot: number): number {
    const iLo = iteration >>> 0;
    const iHi = Math.floor(iteration / 4294967296) >>> 0;
    const w = this.words;
    philox4x32(this.k0, this.k1, iLo, iHi, slot >>> 0, 0, w);
    return toUnit53(w[0], w[1]);
  }

  /**
   * Two independent uniforms from a single Philox invocation.
   *
   * Philox emits 4 words per call and a 53-bit uniform consumes 2, so serving
   * one variable per call throws half of every invocation away. Pairing
   * variables halves the PRNG cost of a run — and in a Monte Carlo engine the
   * PRNG is the single largest line item, ahead of both expression evaluation
   * and accumulation.
   *
   * `pairSlot` addresses the pair, so variables 2k and 2k+1 share slot k.
   */
  uniformPair(iteration: number, pairSlot: number, dst: Float64Array): void {
    const iLo = iteration >>> 0;
    const iHi = Math.floor(iteration / 4294967296) >>> 0;
    const w = this.words;
    philox4x32(this.k0, this.k1, iLo, iHi, pairSlot >>> 0, 0, w);
    dst[0] = toUnit53(w[0], w[1]);
    dst[1] = toUnit53(w[2], w[3]);
  }
}

/**
 * Slot namespace.
 *
 * Variables occupy slots [0, 2^31). Correlation-group common-mode draws live
 * above that line so adding a group can never renumber a variable's slot and
 * silently change every previously saved result.
 */
export const GROUP_SLOT_BASE = 0x80000000;
